import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// material
import { Container, Stack, Button, Typography, Grid, Card, CircularProgress } from '@mui/material';
// components
import { LoadingButton } from '@mui/lab';
import Page from '../components/Page';
import { ProductCard } from '../sections/@dashboard/products';
// mock
import { getServices } from '../store/actions/services';
import { updateServiceImages, convertServicesBatchToV2 } from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';
import { hasPermission, PERMISSIONS } from '../utils/hasPermission';

// ----------------------------------------------------------------------

export const groupBy = (key) => (array) =>
  array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key];
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
    return objectsByKeyValue;
  }, {});

const groupByCategory = groupBy('category');

const PRODUCT_FILTER = {
  DATA: 'Data',
  AIRTIME: 'Airtime',
  ELECTRICITY: 'Electricity',
  CABLE: 'Cable TV',
  SCHOOL: 'School PIN',
};

export default function EcommerceShop() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const { services } = useSelector((state) => state.services);
  const [currentTab, setCurrentTab] = useState(PRODUCT_FILTER.DATA);
  const [loadingImages, setLoadingImages] = React.useState(false);
  const [converting, setConverting] = useState(false);

  // Filter ONLY legacy services (exclude products that are already on V2 engine)
  const legacyServices = useMemo(() => services.filter((d) => !d.useV2Engine), [services]);
  const data = useMemo(() => legacyServices.filter((d) => d.service === 'Data'), [legacyServices]);
  const cable = useMemo(() => legacyServices.filter((d) => d.service === 'Cable'), [legacyServices]);
  const exam = useMemo(() => legacyServices.filter((d) => d.service === 'ResultCheck'), [legacyServices]);
  const electricity = useMemo(() => legacyServices.filter((d) => d.service === 'Electricity'), [legacyServices]);
  const airtime = useMemo(() => legacyServices.filter((d) => d.service === 'Airtime'), [legacyServices]);

  const dataService = useMemo(() => groupByCategory(data), [data]);
  const cableService = useMemo(() => groupByCategory(cable), [cable]);
  const examService = useMemo(() => groupByCategory(exam), [exam]);
  const electricityService = useMemo(() => groupByCategory(electricity), [electricity]);
  const airtimeService = useMemo(() => groupByCategory(airtime), [airtime]);

  const navigateToServiceCategory = (path, service) => {
    navigate(`/dashboard/services-legacy/${encodeURIComponent(path)}?currentService=${encodeURIComponent(service)}`);
  };

  const handleUpdateServiceImages = async () => {
    setLoadingImages(true);
    const response = await updateServiceImages(BRAND_IMAGES);
    if (response.ok) {
      toast.success(response.data.message);
      setLoadingImages(false);
      dispatch(getServices());
      return;
    }

    toast.error(response.data.message);
    setLoadingImages(false);
  };

  const handleConvertListToV2 = async (listToConvert, label) => {
    if (!listToConvert || listToConvert.length === 0) return;
    setConverting(true);
    try {
      const { successCount, total } = await convertServicesBatchToV2(listToConvert);
      toast.success(`Successfully converted ${successCount} of ${total} ${label || ''} products to V2!`);
      dispatch(getServices());
    } catch (err) {
      toast.error('Failed to convert products to V2');
    } finally {
      setConverting(false);
    }
  };

  React.useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  const tabCounts = {
    [PRODUCT_FILTER.DATA]: data.length,
    [PRODUCT_FILTER.AIRTIME]: airtime.length,
    [PRODUCT_FILTER.ELECTRICITY]: electricity.length,
    [PRODUCT_FILTER.CABLE]: cable.length,
    [PRODUCT_FILTER.SCHOOL]: exam.length,
  };

  return (
    <Page title="Services (Legacy) | Dashboard">
      <Container>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          mb={2}
          gap={2}
        >
          <div>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700 }}>
              Services (Legacy)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Original service management view. Showing {legacyServices.length} legacy service{legacyServices.length === 1 ? '' : 's'} remaining.
            </Typography>
          </div>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {legacyServices.length > 0 && (
              <Button
                variant="contained"
                onClick={() => handleConvertListToV2(legacyServices, 'all')}
                disabled={converting}
                sx={{
                  bgcolor: '#16A34A',
                  color: '#FFFFFF',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#15803D' },
                }}
              >
                {converting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  `⚡ Convert All (${legacyServices.length}) to V2`
                )}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/services')}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#0066FE',
                color: '#0066FE',
                '&:hover': {
                  borderColor: '#0052CC',
                  bgcolor: '#EFF6FF',
                },
              }}
            >
              Go to Services (V2)
            </Button>
          </Stack>
        </Stack>

        {legacyServices.length === 0 && (
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: '16px',
              bgcolor: '#F0FDF4',
              border: '1px solid #BBF7D0',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <div>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#166534' }}>
                  All services are upgraded to V2 Multi-Processor Engine! 🎉
                </Typography>
                <Typography variant="body2" sx={{ color: '#15803D' }}>
                  There are no legacy services remaining. All your services are now running on V2 with failover routing.
                </Typography>
              </div>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard/services')}
                sx={{
                  bgcolor: '#16A34A',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#15803D' },
                }}
              >
                Go to Services (V2)
              </Button>
            </Stack>
          </Card>
        )}

        {hasPermission(user, PERMISSIONS.SERVICES_WRITE) && (
          <LoadingButton
            loading={loadingImages}
            disabled={loadingImages}
            variant="contained"
            onClick={handleUpdateServiceImages}
            sx={{ color: 'white', mt: 1 }}
          >
            Update Images
          </LoadingButton>
        )}

        <Grid container spacing={1} sx={{ mt: 2 }}>
          {['Data', 'Airtime', 'Electricity', 'Cable TV', 'School PIN'].map((item) => {
            const count = tabCounts[item] || 0;
            const isSelected = currentTab === item;
            return (
              <Grid key={item} item xs={6} sm={6} md={2.4}>
                <Card
                  onClick={() => setCurrentTab(item)}
                  sx={{
                    width: '100%',
                    height: '48px',
                    borderWidth: isSelected ? 2 : 1,
                    borderStyle: 'solid',
                    borderColor: isSelected ? '#0066FE' : '#E5E7EB',
                    bgcolor: isSelected ? '#EFF6FF' : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Stack
                    sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? '#0066FE' : '#374151',
                      }}
                    >
                      {item} ({count})
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {PRODUCT_FILTER.DATA === currentTab && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ my: 2.5 }}
              gap={1}
            >
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Manage Data Service (Legacy)
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {data.length} legacy product{data.length === 1 ? '' : 's'} remaining
                </Typography>
              </div>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {data.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={() => handleConvertListToV2(data, 'Data')}
                    disabled={converting}
                    sx={{
                      bgcolor: '#16A34A',
                      color: '#FFFFFF',
                      borderRadius: '20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.825rem',
                      '&:hover': { bgcolor: '#15803D' },
                    }}
                  >
                    {converting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      `⚡ Convert All Data (${data.length}) to V2`
                    )}
                  </Button>
                )}
                {hasPermission(user, PERMISSIONS.SERVICES_WRITE) && (
                  <Button
                    sx={{ color: 'white', borderRadius: '20px', textTransform: 'none', fontSize: '0.825rem' }}
                    variant="contained"
                    onClick={() => navigate('/dashboard/services/create')}
                  >
                    Create new Plan
                  </Button>
                )}
              </Stack>
            </Stack>

            {data.length === 0 ? (
              <Card
                sx={{
                  p: 5,
                  textAlign: 'center',
                  borderRadius: '16px',
                  bgcolor: '#F9FAFB',
                  border: '1px dashed #D1D5DB',
                  my: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  All Data services migrated to V2! 🎉
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                  No legacy Data products remain. Manage and route your Data bundles in the new Services V2 catalog.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/dashboard/services/category/data')}
                  sx={{ borderRadius: '20px', bgcolor: '#0066FE', textTransform: 'none' }}
                >
                  Open Data in Services (V2)
                </Button>
              </Card>
            ) : (
              <Grid container spacing={'10px'}>
                {Object.entries(dataService).map(([key, value]) => (
                  <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                    <ProductCard title={key} imageUrl={value[0].imageUrl} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {PRODUCT_FILTER.AIRTIME === currentTab && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ my: 2.5 }}
              gap={1}
            >
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Manage Airtime Service (Legacy)
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {airtime.length} legacy product{airtime.length === 1 ? '' : 's'} remaining
                </Typography>
              </div>
              {airtime.length > 0 && (
                <Button
                  variant="contained"
                  onClick={() => handleConvertListToV2(airtime, 'Airtime')}
                  disabled={converting}
                  sx={{
                    bgcolor: '#16A34A',
                    color: '#FFFFFF',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.825rem',
                    '&:hover': { bgcolor: '#15803D' },
                  }}
                >
                  {converting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    `⚡ Convert All Airtime (${airtime.length}) to V2`
                  )}
                </Button>
              )}
            </Stack>

            {airtime.length === 0 ? (
              <Card
                sx={{
                  p: 5,
                  textAlign: 'center',
                  borderRadius: '16px',
                  bgcolor: '#F9FAFB',
                  border: '1px dashed #D1D5DB',
                  my: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  All Airtime services migrated to V2! 🎉
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                  No legacy Airtime products remain. Manage Airtime in the new Services V2 catalog.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/dashboard/services/category/airtime')}
                  sx={{ borderRadius: '20px', bgcolor: '#0066FE', textTransform: 'none' }}
                >
                  Open Airtime in Services (V2)
                </Button>
              </Card>
            ) : (
              <Grid container spacing={'10px'}>
                {Object.entries(airtimeService).map(([key, value]) => (
                  <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                    <ProductCard title={key} imageUrl={value[0].imageUrl} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {PRODUCT_FILTER.ELECTRICITY === currentTab && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ my: 2.5 }}
              gap={1}
            >
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Manage Electricity Service (Legacy)
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {electricity.length} legacy product{electricity.length === 1 ? '' : 's'} remaining
                </Typography>
              </div>
              {electricity.length > 0 && (
                <Button
                  variant="contained"
                  onClick={() => handleConvertListToV2(electricity, 'Electricity')}
                  disabled={converting}
                  sx={{
                    bgcolor: '#16A34A',
                    color: '#FFFFFF',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.825rem',
                    '&:hover': { bgcolor: '#15803D' },
                  }}
                >
                  {converting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    `⚡ Convert All Electricity (${electricity.length}) to V2`
                  )}
                </Button>
              )}
            </Stack>

            {electricity.length === 0 ? (
              <Card
                sx={{
                  p: 5,
                  textAlign: 'center',
                  borderRadius: '16px',
                  bgcolor: '#F9FAFB',
                  border: '1px dashed #D1D5DB',
                  my: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  All Electricity services migrated to V2! 🎉
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                  No legacy Electricity products remain. Manage DISCO providers and tokens in Services V2.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/dashboard/services/category/electricity')}
                  sx={{ borderRadius: '20px', bgcolor: '#0066FE', textTransform: 'none' }}
                >
                  Open Electricity in Services (V2)
                </Button>
              </Card>
            ) : (
              <Grid container spacing={'10px'}>
                {Object.entries(electricityService).map(([key, value]) => (
                  <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                    <ProductCard title={key} imageUrl={value[0].imageUrl} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {PRODUCT_FILTER.CABLE === currentTab && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ my: 2.5 }}
              gap={1}
            >
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Manage TV Cable Service (Legacy)
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {cable.length} legacy product{cable.length === 1 ? '' : 's'} remaining
                </Typography>
              </div>
              {cable.length > 0 && (
                <Button
                  variant="contained"
                  onClick={() => handleConvertListToV2(cable, 'Cable TV')}
                  disabled={converting}
                  sx={{
                    bgcolor: '#16A34A',
                    color: '#FFFFFF',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.825rem',
                    '&:hover': { bgcolor: '#15803D' },
                  }}
                >
                  {converting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    `⚡ Convert All Cable (${cable.length}) to V2`
                  )}
                </Button>
              )}
            </Stack>

            {cable.length === 0 ? (
              <Card
                sx={{
                  p: 5,
                  textAlign: 'center',
                  borderRadius: '16px',
                  bgcolor: '#F9FAFB',
                  border: '1px dashed #D1D5DB',
                  my: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  All Cable TV services migrated to V2! 🎉
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                  No legacy Cable TV products remain. Manage Cable TV packages in Services V2.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/dashboard/services/category/cable')}
                  sx={{ borderRadius: '20px', bgcolor: '#0066FE', textTransform: 'none' }}
                >
                  Open Cable TV in Services (V2)
                </Button>
              </Card>
            ) : (
              <Grid container spacing={'10px'}>
                {Object.entries(cableService).map(([key, value]) => (
                  <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                    <ProductCard title={key} imageUrl={value[0].imageUrl} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {PRODUCT_FILTER.SCHOOL === currentTab && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ my: 2.5 }}
              gap={1}
            >
              <div>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Manage School Result Check Service (Legacy)
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {exam.length} legacy product{exam.length === 1 ? '' : 's'} remaining
                </Typography>
              </div>
              {exam.length > 0 && (
                <Button
                  variant="contained"
                  onClick={() => handleConvertListToV2(exam, 'Result Checker')}
                  disabled={converting}
                  sx={{
                    bgcolor: '#16A34A',
                    color: '#FFFFFF',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.825rem',
                    '&:hover': { bgcolor: '#15803D' },
                  }}
                >
                  {converting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    `⚡ Convert All Exams (${exam.length}) to V2`
                  )}
                </Button>
              )}
            </Stack>

            {exam.length === 0 ? (
              <Card
                sx={{
                  p: 5,
                  textAlign: 'center',
                  borderRadius: '16px',
                  bgcolor: '#F9FAFB',
                  border: '1px dashed #D1D5DB',
                  my: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  All Result Checker services migrated to V2! 🎉
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
                  No legacy Result Check products remain. Manage e-PINs in Services V2.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/dashboard/services/category/resultcheck')}
                  sx={{ borderRadius: '20px', bgcolor: '#0066FE', textTransform: 'none' }}
                >
                  Open Result Checkers in Services (V2)
                </Button>
              </Card>
            ) : (
              <Grid container spacing={'10px'}>
                {Object.entries(examService).map(([key, value]) => (
                  <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                    <ProductCard title={key} imageUrl={value[0].imageUrl} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>
    </Page>
  );
}
